if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
 
        this.hideVariants = this.getAttribute("data-hide-variants") === "true";
        this.currentVariantId = this.getAttribute("data-current-variant-id");
        this.currentVariantSku = this.getAttribute("data-current-variant-sku") || "";
        this.productId = this.getAttribute("data-product-id");
 
        // Parse variant map: { variantId: { mediaId, sku } }
        this.variantMediaMap = {};
        const mapEl = this.querySelector(".variant-media-map");
        if (mapEl) {
          try {
            this.variantMediaMap = JSON.parse(mapEl.textContent);
          } catch (e) {
            console.warn("[media-gallery] Could not parse variant media map", e);
          }
        }
 
        // Build a set of ALL variant SKUs (used to decide if a media alt's SKU
        // belongs to ANY variant — if so it's a "variant-specific" image).
        this.allVariantSkus = new Set(
          Object.values(this.variantMediaMap)
            .map((v) => (v && v.sku ? String(v.sku) : ""))
            .filter(Boolean),
        );
 
        this.elements = {
          mains: this.querySelectorAll(".product-media-main"),
          thumbnails: this.querySelectorAll(
            ".product-media-thumbnail:not(.product-media-thumbnail-variant), .product-media-thumbnail:first-child",
          ),
          allThumbnails: this.querySelectorAll(".product-media-thumbnail"),
          slideshow: this.querySelector(".product-media-main-wrapper"),
        };
 
        if (!this.elements.thumbnails.length) {
          return;
        }
 
        // Annotate thumbnails with their SKU (extracted from img alt text)
        // so the filter can match them too. We do this once at construction.
        this.annotateThumbnailSkus();
 
        this.elements.thumbnails.forEach((thumbnail) => {
          const mediaToggle = thumbnail.querySelector("button");
          if (!mediaToggle) {
            return;
          }
          mediaToggle.addEventListener(
            "click",
            this.setActiveMedia.bind(this, mediaToggle.dataset.mediaId, false),
          );
        });
 
        this.onVariantChanged = this.onVariantChanged.bind(this);
        this.onVariantInputChange = this.onVariantInputChange.bind(this);
 
        document.addEventListener("variant:changed", this.onVariantChanged);
        this.observeVariantInput();
      }
 
      annotateThumbnailSkus() {
        // For each thumbnail, parse the SKU out of the img alt text
        // (after the last "-") and set data-media-sku.
        this.elements.allThumbnails.forEach((thumb) => {
          if (thumb.dataset.mediaSku) return; // already set by Liquid
 
          const img = thumb.querySelector("img");
          const alt = img && img.getAttribute("alt");
          if (!alt) return;
 
          const parts = alt.split("-");
          if (parts.length < 2) return; // no SKU suffix
 
          const sku = parts[parts.length - 1].trim();
          thumb.dataset.mediaSku = sku;
        });
      }
 
      observeVariantInput() {
        const variantInputs = document.querySelectorAll(
          'form[action*="/cart/add"] input[name="id"], product-form input[name="id"]',
        );
        variantInputs.forEach((input) => {
          const observer = new MutationObserver(() => {
            this.onVariantInputChange(input.value);
          });
          observer.observe(input, { attributes: true, attributeFilter: ["value"] });
 
          input.addEventListener("change", () => {
            this.onVariantInputChange(input.value);
          });
        });
      }
 
      onVariantInputChange(newVariantId) {
        if (!newVariantId || String(newVariantId) === String(this.currentVariantId)) {
          return;
        }
        this.updateForVariant(newVariantId);
      }
 
      onVariantChanged(event) {
        const variant = event.detail && event.detail.variant;
        if (!variant) return;
        this.updateForVariant(variant.id, variant.sku);
      }
 
      updateForVariant(variantId, skuHint) {
        this.currentVariantId = String(variantId);
 
        // Resolve SKU from the map (most reliable) or from the hint
        const variantData = this.variantMediaMap[this.currentVariantId];
        this.currentVariantSku =
          (variantData && variantData.sku) || skuHint || "";
 
        if (this.hideVariants) {
          this.filterMediaForVariant();
        }
 
        // Switch the active media to the variant's featured media
        const variantMediaId = variantData && variantData.mediaId;
        if (variantMediaId) {
          this.setActiveMedia(String(variantMediaId), true);
        }
      }
 
      connectedCallback() {
        if (this.hideVariants) {
          this.filterMediaForVariant();
        }
 
        if (!window.Flickity) {
          return;
        }
 
        this.initFlickity();
      }
 
      disconnectedCallback() {
        document.removeEventListener("variant:changed", this.onVariantChanged);
        if (this.flickity) {
          this.flickity.destroy();
          this.flickity = null;
        }
      }
 
      initFlickity() {
        this.elements.slideshow.style.display = "block";
        this.flickity = new Flickity(this.elements.slideshow, {
          autoPlay: false,
          cellAlign: "center",
          groupCells: false,
          contain: true,
          resize: true,
          draggable: true,
          prevNextButtons: false,
          cellSelector: ".product-media:not(.is-hidden-variant)",
          initialIndex: 0,
          pageDots: false,
          wrapAround: true,
          accessibility: false,
          adaptiveHeight: true,
          watchCSS: true,
          on: {
            change: (index) => {
              const visibleCells = this.flickity.cells;
              if (!visibleCells[index]) return;
              const { element } = visibleCells[index];
              const mediaId = element.dataset.mediaId;
              this.setActiveMedia(mediaId);
            },
          },
        });
      }
 
      filterMediaForVariant() {
        const currentSku = String(this.currentVariantSku || "");
 
        // Filter main media
        this.elements.mains.forEach((mediaEl) => {
          const mediaSku = mediaEl.dataset.mediaSku || "";
 
          if (!mediaSku) {
            // No SKU on this media: it's a general/shared image. Always show.
            this.showMedia(mediaEl);
            return;
          }
 
          // If this media's SKU isn't a known variant SKU, treat it as general.
          if (!this.allVariantSkus.has(mediaSku)) {
            this.showMedia(mediaEl);
            return;
          }
 
          // Belongs to a variant: show only if it's the current one
          if (mediaSku === currentSku) {
            this.showMedia(mediaEl);
          } else {
            this.hideMedia(mediaEl);
          }
        });
 
        // Same filter on thumbnails
        this.elements.allThumbnails.forEach((thumbEl) => {
          const mediaSku = thumbEl.dataset.mediaSku || "";
 
          if (!mediaSku) {
            this.showThumb(thumbEl);
            return;
          }
 
          if (!this.allVariantSkus.has(mediaSku)) {
            this.showThumb(thumbEl);
            return;
          }
 
          if (mediaSku === currentSku) {
            this.showThumb(thumbEl);
          } else {
            this.hideThumb(thumbEl);
          }
        });
 
        if (this.flickity) {
          this.flickity.reloadCells();
          this.flickity.resize();
        }
      }
 
      showMedia(el) {
        el.classList.remove("is-hidden-variant");
        el.style.display = "";
      }
 
      hideMedia(el) {
        el.classList.add("is-hidden-variant");
        el.style.display = "none";
        el.classList.remove("is-active");
      }
 
      showThumb(el) {
        el.classList.remove("is-hidden-variant");
        el.style.display = "";
      }
 
      hideThumb(el) {
        el.classList.add("is-hidden-variant");
        el.style.display = "none";
      }
 
      setActiveMedia(mediaId, prepend) {
        const activeMainMedia = [...this.elements.mains].find((element) => {
          return element.classList.contains("is-active");
        });
        const nextMainMedia = [...this.elements.mains].find((element) => {
          return element.dataset.mediaId === mediaId.toString();
        });
 
        if (!nextMainMedia) return;
 
        if (nextMainMedia.classList.contains("is-hidden-variant")) {
          this.showMedia(nextMainMedia);
        }
 
        const index = [...this.elements.mains].indexOf(nextMainMedia);
 
        if (this.flickity) {
          this.flickity.select(index);
        }
 
        this.elements.thumbnails.forEach((thumb) => {
          thumb.classList.remove("is-active");
        });
        const thumbnail = [...this.elements.thumbnails].find((element) => {
          return element.dataset.mediaId === mediaId.toString();
        });
 
        if (activeMainMedia) {
          activeMainMedia.classList.remove("is-active");
        }
 
        nextMainMedia.classList.add("is-active");
        thumbnail?.classList.add("is-active");
        this.playActiveMedia(nextMainMedia);
 
        if (!thumbnail) {
          return;
        }
 
        if (prepend) {
          thumbnail.parentElement.prepend(thumbnail);
        } else {
          const container = thumbnail.parentElement;
          const containerRect = container.getBoundingClientRect();
          const thumbnailRect = thumbnail.getBoundingClientRect();
 
          const isHorizontalScroll = container.scrollWidth > container.clientWidth;
 
          if (isHorizontalScroll) {
            if (thumbnailRect.left < containerRect.left) {
              container.scrollBy({
                left: thumbnailRect.left - containerRect.left,
                behavior: "smooth",
              });
            } else if (thumbnailRect.right > containerRect.right) {
              container.scrollBy({
                left: thumbnailRect.right - containerRect.right,
                behavior: "smooth",
              });
            }
          } else {
            if (thumbnailRect.top < containerRect.top) {
              container.scrollBy({
                top: thumbnailRect.top - containerRect.top,
                behavior: "smooth",
              });
            } else if (thumbnailRect.bottom > containerRect.bottom) {
              container.scrollBy({
                top: thumbnailRect.bottom - containerRect.bottom,
                behavior: "smooth",
              });
            }
          }
        }
      }
 
      playActiveMedia(activeItem) {
        if (typeof window.pauseAllMedia === "function") {
          window.pauseAllMedia();
        }
        const deferredMedia = activeItem.querySelector("deferred-media");
 
        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }
    },
  );
}
 
if (!customElements.get("product-thumbnail-navigation")) {
  customElements.define(
    "product-thumbnail-navigation",
    class ProductThumbnailNavigation extends HTMLElement {
      constructor() {
        super();
      }
 
      connectedCallback() {
        this.init();
      }
 
      init() {
        this.gallery = document.querySelector(`#${this.getAttribute("for")}`);
        this.elements = {
          gallery: this.gallery,
          scroller: this.gallery.querySelector(".product-media-scroller"),
          thumbnailWrapper: this.gallery.querySelector(".product-media-thumbnails"),
          thumbnails: this.gallery.querySelectorAll(".product-media-thumbnail"),
          buttonPrev: this.querySelector(".js-prev"),
          buttonNext: this.querySelector(".js-next"),
        };
 
        if (!this.elements.thumbnails.length) {
          return;
        }
 
        this.isHorizontal =
          this.elements.scroller.clientWidth < this.elements.thumbnailWrapper.scrollWidth;
        const isVertical =
          this.elements.scroller.clientHeight < this.elements.thumbnailWrapper.scrollHeight;
 
        if (!this.isHorizontal && !isVertical) {
          this.elements.scroller.classList.add("no-overflow");
        } else {
          this.elements.scroller.classList.remove("no-overflow");
        }
 
        if (this.isHorizontal || isVertical) {
          this.classList.remove("hidden");
 
          this.elements.buttonPrev.addEventListener("click", (event) => {
            event.preventDefault();
            this.prev();
          });
 
          this.elements.buttonNext.addEventListener("click", (event) => {
            event.preventDefault();
            this.next();
          });
 
          this.elements.thumbnailWrapper.addEventListener("scroll", () => {
            this.buttonAvailability();
          });
        }
      }
 
      prev() {
        if (this.isHorizontal) {
          const scrollPos = this.elements.thumbnailWrapper.scrollLeft;
          this.elements.thumbnailWrapper.scroll(scrollPos - 300, 0);
        } else {
          const scrollPos = this.elements.thumbnailWrapper.scrollTop;
          this.elements.thumbnailWrapper.scroll(0, scrollPos - 300);
        }
      }
 
      next() {
        if (this.isHorizontal) {
          const scrollPos = this.elements.thumbnailWrapper.scrollLeft;
          this.elements.thumbnailWrapper.scroll(scrollPos + 300, 0);
        } else {
          const scrollPos = this.elements.thumbnailWrapper.scrollTop;
          this.elements.thumbnailWrapper.scroll(0, scrollPos + 300);
        }
      }
 
      buttonAvailability() {
        const el = this.elements.thumbnailWrapper;
        let hasBottomed, hasTopped;
 
        if (this.isHorizontal) {
          hasBottomed = el.scrollLeft + el.clientWidth >= el.scrollWidth - 30;
          hasTopped = el.scrollLeft <= 30;
        } else {
          hasBottomed = el.scrollTop + el.clientHeight >= el.scrollHeight - 30;
          hasTopped = el.scrollTop <= 30;
        }
 
        this.elements.buttonPrev.classList.remove("pill-nav-item-disabled");
        this.elements.buttonNext.classList.remove("pill-nav-item-disabled");
 
        if (hasBottomed) {
          this.elements.buttonNext.classList.add("pill-nav-item-disabled");
        }
 
        if (hasTopped) {
          this.elements.buttonPrev.classList.add("pill-nav-item-disabled");
        }
      }
    },
  );
}
 
if (!customElements.get("media-zoom")) {
  customElements.define(
    "media-zoom",
    class MediaZoom extends HTMLElement {
      constructor() {
        super();
 
        this.mode = this.getAttribute("data-mode");
        this.zoomWidth = parseInt(this.getAttribute("data-zoom-width"), 10) || 150;
        this.zoomHeight = parseInt(this.getAttribute("data-zoom-height"), 10) || 150;
 
        this.lastEvent = null;
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onClick = this.onClick.bind(this);
      }
 
      connectedCallback() {
        this.innerHTML += `<div class="media-zoom-zoom-area"></div>`;
        this.original = this.querySelector("img");
        this.zoomArea = this.querySelector(".media-zoom-zoom-area");
 
        this.original.addEventListener("mouseenter", this.onMouseEnter);
        this.original.addEventListener("mousemove", this.onMouseMove);
        this.original.addEventListener("mouseleave", this.onMouseLeave);
 
        if (this.mode === "lightbox_zoom_hover") {
          this.original.addEventListener("click", this.onClick);
        }
 
        this.zoomArea.style.width = `${this.zoomWidth}px`;
        this.zoomArea.style.height = `${this.zoomHeight}px`;
 
        this.preview = document.createElement("div");
        this.preview.classList.add("media-zoom-preview-area", "color-background-1");
        this.preview.innerHTML = '<div class="spinner"></div>';
        document.body.appendChild(this.preview);
      }
 
      disconnectedCallback() {
        this.original.removeEventListener("mouseenter", this.onMouseEnter);
        this.original.removeEventListener("mousemove", this.onMouseMove);
        this.original.removeEventListener("mouseleave", this.onMouseLeave);
        document.body.removeChild(this.preview);
      }
 
      onMouseEnter() {
        this.zoomArea.style.display = "block";
        this.preview.style.display = "block";
 
        const previewSize = `${this.original.offsetWidth}px`;
        this.preview.style.width = previewSize;
        this.preview.style.height = previewSize;
 
        if (!this.previewImg) {
          this.previewImg = new Image();
          this.previewImg.src = this.getAttribute("data-zoom");
          this.previewImg.onload = () => {
            const zoomScale = this.original.offsetWidth / this.zoomWidth;
 
            this.previewImg.style.width = `${this.original.offsetWidth * zoomScale}px`;
            this.previewImg.style.height = `${this.original.offsetHeight * zoomScale}px`;
 
            this.preview.innerHTML = "";
            this.preview.appendChild(this.previewImg);
            if (this.lastEvent) {
              this.original.dispatchEvent(new MouseEvent("mousemove", this.lastEvent));
            }
          };
        } else {
          const zoomScale = this.original.offsetWidth / this.zoomWidth;
          this.previewImg.style.width = `${this.original.offsetWidth * zoomScale}px`;
          this.previewImg.style.height = `${this.original.offsetHeight * zoomScale}px`;
        }
      }
 
      onMouseMove(e) {
        this.lastEvent = e;
        const { left, top, width, height } = this.original.getBoundingClientRect();
 
        const x = e.pageX - (window.pageXOffset + left);
        const y = e.pageY - (window.pageYOffset + top);
 
        const zoomX = Math.min(Math.max(x - this.zoomWidth / 2, 0), width - this.zoomWidth);
        const zoomY = Math.min(Math.max(y - this.zoomHeight / 2, 0), height - this.zoomHeight);
 
        this.zoomArea.style.left = `${zoomX}px`;
        this.zoomArea.style.top = `${zoomY}px`;
 
        this.preview.style.position = "fixed";
        this.preview.style.left = `${left + width + 50}px`;
        this.preview.style.top = `${top - 19}px`;
 
        if (this.previewImg && this.previewImg.complete) {
          const previewX = (zoomX / width) * this.previewImg.width;
          const previewY = (zoomY / height) * this.previewImg.height;
 
          this.previewImg.style.transform = `translate(${-previewX}px, ${-previewY}px)`;
        }
      }
 
      onMouseLeave() {
        this.zoomArea.style.display = "none";
        this.preview.style.display = "none";
      }
 
      onClick() {
        const sectionId = this.getAttribute("data-modal-id");
        const modal = document.querySelector(`#ProductModal-${sectionId}`);
 
        if (modal) {
          modal.show(this);
        }
      }
    },
  );
}
 